import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from './env';

// エラーコード別日本語メッセージマッピング
export const ERROR_MESSAGES: Record<string, string> = {
  AUTH_LOGIN_INVALID_CREDENTIALS: 'メールアドレスまたはパスワードが正しくありません',
  AUTH_LOGIN_ACCOUNT_DISABLED: 'アカウントが無効化されています',
  AUTH_LOGIN_DOMAIN_NOT_ALLOWED: 'このメールアドレスのドメインではアカウントを作成できません',
  AUTH_LOGIN_INVALID_REQUEST: '無効なリクエストです',
  AUTH_LOGIN_INVALID_EMAIL: 'メールアドレスの形式が正しくありません',
  AUTH_LOGIN_INVALID_PASSWORD: 'パスワードは8文字以上である必要があります',
  AUTH_LOGIN_SERVER_ERROR: 'ログイン処理中にエラーが発生しました',
  AUTH_LOGIN_RATE_LIMITED: 'ログイン試行回数が上限に達しました。しばらく経ってから再度お試しください',
  USER_NOT_FOUND: 'ユーザーが見つかりません',
  USER_ALREADY_EXISTS: 'このメールアドレスは既に登録されています',
  UNAUTHORIZED: '認証が必要です',
  FILE_NOT_FOUND: 'ファイルが見つかりません',
  FILE_ACCESS_DENIED: 'このファイルにアクセスする権限がありません',
  FILE_UPLOAD_SIZE_EXCEEDED: 'ファイルサイズが上限（5GB）を超えています',
  FILE_SIZE_EXCEEDS_LIMIT: 'ファイルサイズが上限（5GB）を超えています',
  FILE_NOT_AVAILABLE: 'このファイルは利用できません（ウイルススキャン未完了または失敗の可能性があります）',
  FILE_SHARE_NOT_SCANNABLE: 'セキュリティスキャンができないため共有できません。パスワード付きzipや暗号化ファイルは共有できません。',
  SHARE_EXPIRED: '共有リンクの有効期限が切れています',
  SHARE_REVOKED: '共有リンクは無効化されています',
  INVALID_TOKEN: 'トークンが無効です',
  INVALID_NONCE: '認証リクエストの検証に失敗しました。お手数ですが、最初からログインをやり直してください',
  TOKEN_EXPIRED: 'トークンの有効期限が切れています',
  TOKEN_RESET_INVALID: 'リセットリンクが無効または期限切れです',
  TOKEN_RESET_EXPIRED: 'リセットリンクが無効または期限切れです',
  PASSWORD_RESET_RATE_LIMITED: 'リクエストが多すぎます。しばらく経ってから再度お試しください',
  PASSWORD_POLICY_VIOLATION: 'パスワードがポリシー要件を満たしていません',
  CURRENT_PASSWORD_INCORRECT: '現在のパスワードが正しくありません',
  PASSWORD_CHANGE_RATE_LIMITED: 'パスワード変更のリクエストが多すぎます。しばらく経ってから再度お試しください',
  PASSWORD_CHANGE_NOT_ALLOWED_OIDC: 'パスワードの変更は認証プロバイダーで行ってください',
  TOKEN_REFRESH_EXPIRED: 'セッションの有効期限が切れました。再度ログインしてください',
  TOKEN_REFRESH_INVALID: 'セッションが無効です。再度ログインしてください',
  TOKEN_REFRESH_REQUIRED: 'セッションの更新に失敗しました',
  SHARE_EXPIRY_EXCEEDS_MAX_PERIOD: '有効期限は共有作成日時から30日以内である必要があります',
  SHARE_EXPIRY_MUST_BE_FUTURE: '有効期限は現在時刻より未来である必要があります',
  SHARE_EXPIRY_CANNOT_UPDATE_INACTIVE: '無効化済みまたは期限切れの共有の有効期限は変更できません',
  SHARE_EXPIRY_SHARE_NOT_FOUND: '共有が見つかりません',
  SHARE_EXPIRY_FORBIDDEN: 'この共有を変更する権限がありません',
  ADMIN_LOGS_INVALID_FILTER: '検索条件に使用できない文字または不正な形式が含まれています',
  NETWORK_ERROR: 'ネットワークエラーが発生しました',
  INTERNAL_SERVER_ERROR: 'サーバーエラーが発生しました。しばらく経ってから再度お試しください',
  DEFAULT: '予期しないエラーが発生しました'
};

// Axios インスタンス作成
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000,
  withCredentials: true
});

// エラーメッセージ取得ヘルパー（エラーコード→定義済み日本語メッセージ）
export const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const errorCode = error.response?.data?.error?.code;
    return ERROR_MESSAGES[errorCode] || ERROR_MESSAGES.DEFAULT;
  }
  return ERROR_MESSAGES.DEFAULT;
};

// CR-054: サーバが返した error.message をそのまま取得するヘルパー。
// any を使わず axios.isAxiosError でガードし、取得できなければ fallback を返す。
export const getServerErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.error?.message || fallback;
  }
  return fallback;
};

// インターセプター設定
// refreshTokenはhttpOnly Cookieで自動送信されるため、明示的な管理不要
export const setupInterceptors = (
  getToken: () => string | null,
  updateToken: (newToken: string) => void,
  logout: () => void
) => {
  // CR-044: 進行中のリフレッシュを共有する single-flight。
  // 複数リクエストが同時に401になっても refresh は1回だけ呼ぶ。
  // 回転式リフレッシュトークンでも後続リクエストが古いトークンで失敗→不要ログアウト、を防ぐ。
  let refreshPromise: Promise<string> | null = null;

  const performRefresh = (): Promise<string> => {
    if (!refreshPromise) {
      // refreshTokenはhttpOnly Cookieで自動送信（withCredentials: true）
      refreshPromise = apiClient
        .post('/api/v1/auth/refresh')
        .then((response) => {
          const newToken = response.data.jwt;
          updateToken(newToken);
          return newToken as string;
        })
        .finally(() => {
          // 成否に関わらず次回のために解放
          refreshPromise = null;
        });
    }
    return refreshPromise;
  };

  // リクエストインターセプター: JWTをAuthorizationヘッダーに追加
  // Bug-023: 呼び出し側が明示的に Authorization を指定している場合は上書きしない。
  //   共有リンク経由(/receive)のダウンロードは OTPセッショントークンを明示指定するが、
  //   ログイン中はここでログインユーザーのアクセストークンに上書きされ、
  //   バックエンドの share-session 検証に失敗して 401 になっていた。
  apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      if (config.headers && !config.headers.Authorization) {
        const token = getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // レスポンスインターセプター: APIレスポンスバリデーション + 401/403時にトークンリフレッシュ
  apiClient.interceptors.response.use(
    (response) => {
      // APIリクエスト（/api/*）のレスポンスがHTMLの場合はエラーとして扱う
      // CloudFrontのCustomErrorResponsesがAPIの正当なエラーをindex.htmlに書き換えた場合の防御
      const contentType = response.headers?.['content-type'] || '';
      const isApiRequest = response.config?.url?.includes('/api/');
      if (isApiRequest && contentType.includes('text/html')) {
        return Promise.reject(new AxiosError(
          'APIリクエストに対してHTMLレスポンスが返されました。サーバー設定を確認してください。',
          'ERR_INVALID_RESPONSE',
          response.config,
          response.request,
          response
        ));
      }
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

      // 認証エンドポイント（login, oidc/callback, refresh）はトークンリフレッシュ対象外。
      // CR-044: refresh 自身を除外し、refresh が401を返した際の再帰呼び出しを防ぐ。
      const requestUrl = originalRequest.url || '';
      const isAuthEndpoint =
        requestUrl.includes('/auth/login') ||
        requestUrl.includes('/auth/oidc/callback') ||
        requestUrl.includes('/auth/refresh');

      // Bug-023: 共有リンク経由(/receive)は OTPセッショントークンで認証しており、
      //   ログインユーザーのJWTリフレッシュ対象ではない。401はOTP再認証を促すべきで、
      //   ここでリフレッシュ＆ログイントークンでリトライすると誤った認証情報を送ってしまう。
      const isReceiveEndpoint = requestUrl.includes('/receive/');

      // Bug-020: アクセストークン(JWT)失効時の自動リフレッシュ対象を判定する。
      // - 401: バックエンドが返す通常の認証エラー。
      // - 403: API Gateway Lambda Authorizer がJWT失効/不正で Deny を返した場合、
      //   API Gateway は execute-api:Invoke 不許可として 403 を返す（AWS既定の
      //   {"message": "User is not authorized..."} 形式でレスポンスbodyに error.code を持たない）。
      //   この 403 はトークン失効が原因なのでリフレッシュで回復可能。
      //   一方、アプリが返す権限エラー(FILE_ACCESS_DENIED 等)は error.code を持つため、
      //   error.code の有無で区別し、Authorizer由来の 403 のみリフレッシュ対象とする。
      const status = error.response?.status;
      const hasAppErrorCode = !!(
        error.response?.data as { error?: { code?: string } } | undefined
      )?.error?.code;
      const isAuthorizerDenied = status === 403 && !hasAppErrorCode;
      const shouldRefresh = status === 401 || isAuthorizerDenied;

      if (shouldRefresh && !originalRequest._retry && !isAuthEndpoint && !isReceiveEndpoint) {
        originalRequest._retry = true;

        try {
          // 進行中のリフレッシュがあれば共有（single-flight）
          const newToken = await performRefresh();

          // 元のリクエストをリトライ
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          return apiClient(originalRequest);

        } catch (refreshError) {
          // リフレッシュトークンも期限切れの場合はログアウト
          logout();
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};

// JWT有効期限監視: 残り5分で自動リフレッシュ
export const setupJwtMonitoring = (
  getToken: () => string | null,
  refreshToken: () => Promise<void>
): (() => void) => {
  const CHECK_INTERVAL = 60000; // 1分ごとにチェック
  const REFRESH_THRESHOLD = 5 * 60; // 5分前にリフレッシュ

  const decodeJwt = (token: string): { exp?: number } | null => {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      const payload = JSON.parse(atob(parts[1]));
      return payload;
    } catch (error) {
      return null;
    }
  };

  const checkTokenExpiration = async () => {
    const token = getToken();
    if (!token) {
      return;
    }

    const payload = decodeJwt(token);
    if (!payload || !payload.exp) {
      return;
    }

    const now = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = payload.exp - now;

    // 有効期限が5分以内、または既に期限切れの場合はリフレッシュ
    if (timeUntilExpiry <= REFRESH_THRESHOLD) {
      try {
        await refreshToken();
      } catch (error) {
        // エラーが発生しても監視は継続
        console.error('JWT refresh failed during monitoring:', error);
      }
    }
  };

  // 定期チェック開始
  const intervalId = setInterval(checkTokenExpiration, CHECK_INTERVAL);

  // 停止関数を返す
  return () => {
    clearInterval(intervalId);
  };
};
